import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Eraser, Power, RotateCcw, Trash2, History, ShieldCheck } from 'lucide-react';
import { API, fmtDate, inputCls } from './groupMeta';

const Btn = ({ onClick, Icon, children, hex, testId }) => (
  <button onClick={onClick} data-testid={testId} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs border transition-colors hover:bg-white/[0.04]"
    style={{ borderColor: `${hex}55`, color: hex }}><Icon className="w-3.5 h-3.5" /> {children}</button>
);

export const GroupRecordsTab = ({ group, getAuthHeaders, onChanged, onDeleted }) => {
  const [reason, setReason] = useState('');
  const h = () => ({ headers: getAuthHeaders() });
  const archived = group.status === 'archived';

  const exportData = async () => {
    try {
      const r = await axios.get(`${API}/groups/${group.id}/export`, h());
      const url = URL.createObjectURL(new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' }));
      const a = document.createElement('a'); a.href = url; a.download = `group-data-${group.name.replace(/\s+/g, '-').toLowerCase()}.json`; a.click();
      URL.revokeObjectURL(url); toast.success('Data export downloaded'); onChanged();
    } catch { toast.error('Export failed'); }
  };
  const call = async (fn, ok) => { try { await fn(); toast.success(ok); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } };
  const erase = () => window.confirm('Anonymise the leader and every staff-list entry? Inspection history is kept. This cannot be undone.') &&
    call(() => axios.post(`${API}/groups/${group.id}/erase-personal-data`, {}, h()), 'Personal data erased');
  const archive = () => window.confirm(`Shut down and archive ${group.name}? It will become read-only.`) &&
    call(() => axios.post(`${API}/groups/${group.id}/archive`, { reason }, h()), 'Group archived');
  const restore = () => call(() => axios.post(`${API}/groups/${group.id}/restore`, {}, h()), 'Group restored');
  const del = async () => {
    if (window.prompt(`Type the group name to permanently delete it and all inspections:\n${group.name}`) !== group.name) return;
    try { await axios.delete(`${API}/groups/${group.id}`, h()); toast.success('Group deleted'); onDeleted(); } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_360px] gap-5" data-testid="group-records-tab">
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.015] p-4">
        <p className="flex items-center gap-2 text-[9px] tracking-[0.2em] uppercase text-white/45 font-semibold mb-3"><History className="w-3.5 h-3.5" /> Audit trail</p>
        <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
          {(group.activity || []).length === 0 ? <p className="text-xs text-white/35">No activity yet.</p> : group.activity.map(a => (
            <div key={a.id} className="flex gap-3 text-xs" data-testid="activity-item">
              <span className="w-1.5 h-1.5 mt-1.5 rounded-full bg-cyan-400/70 flex-shrink-0" />
              <div><p className="text-white/80">{a.action}{a.detail ? <span className="text-white/40"> — {a.detail}</span> : null}</p>
                <p className="text-[10px] text-white/35">{a.actor} · {fmtDate(a.at)} {new Date(a.at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</p></div>
            </div>
          ))}
        </div>
      </div>
      {group.can_edit && (
        <div className="space-y-4">
          <div className="rounded-2xl border border-cyan-400/15 bg-cyan-500/[0.03] p-4 space-y-3">
            <p className="flex items-center gap-2 text-[9px] tracking-[0.2em] uppercase text-cyan-300/80 font-semibold"><ShieldCheck className="w-3.5 h-3.5" /> GDPR & data rights</p>
            <p className="text-[11px] text-white/55">Consent recorded: {group.data_consent ? fmtDate(group.consent_recorded_at) : <span className="text-amber-300">not recorded</span>}
              {group.personal_data_erased_at && <><br />Personal data erased {fmtDate(group.personal_data_erased_at)}</>}</p>
            <div className="flex flex-wrap gap-2">
              <Btn onClick={exportData} Icon={Download} hex="#00E5FF" testId="gdpr-export-btn">Export all data</Btn>
              <Btn onClick={erase} Icon={Eraser} hex="#F59E0B" testId="gdpr-erase-btn">Erase personal data</Btn>
            </div>
          </div>
          <div className="rounded-2xl border border-rose-400/15 bg-rose-500/[0.03] p-4 space-y-3">
            <p className="text-[9px] tracking-[0.2em] uppercase text-rose-300/80 font-semibold">Lifecycle</p>
            {archived ? (
              <>
                <p className="text-[11px] text-white/55">Archived {fmtDate(group.archived_at)} by {group.archived_by}{group.shutdown_reason ? ` — ${group.shutdown_reason}` : ''}</p>
                <Btn onClick={restore} Icon={RotateCcw} hex="#10B981" testId="group-restore-btn">Restore group</Btn>
              </>
            ) : (
              <>
                <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Shutdown reason" className={inputCls} data-testid="archive-reason-input" />
                <Btn onClick={archive} Icon={Power} hex="#F43F5E" testId="group-archive-btn">Shut down & archive</Btn>
              </>
            )}
            <div className="pt-2 border-t border-white/[0.05]"><Btn onClick={del} Icon={Trash2} hex="#94A3B8" testId="group-delete-btn">Delete permanently</Btn></div>
          </div>
        </div>
      )}
    </div>
  );
};
